import type { WebsiteContent } from '../types/content';
import { appConfig } from '../config/appConfig';
import {
  buildConditionalRequestHeaders,
  getContentSignature,
  readContentCacheEntry,
  readResponseValidators,
  touchContentCache,
  writeContentCache,
  type ContentCacheEntry,
} from './contentCache';

function validateContent(json: WebsiteContent): void {
  if (!json.highLevelIssues?.length || !json.index?.byId) {
    throw new Error('Content file is missing highLevelIssues or index.byId (schema v2 required)');
  }
}

async function fetchContent(
  signal: AbortSignal,
  entry?: ContentCacheEntry,
): Promise<Response> {
  return fetch(appConfig.contentJsonPath, {
    signal,
    headers: entry ? buildConditionalRequestHeaders(entry) : undefined,
  });
}

/**
 * Loads content from cache and/or origin.
 * - Within TTL: cache only, no network.
 * - After TTL: conditional request; cache is replaced only when the server reports new content.
 * - force: always downloads and refreshes cache.
 */
export async function loadWebsiteContent(
  options: { force?: boolean; signal?: AbortSignal } = {},
): Promise<WebsiteContent> {
  const { force = false, signal } = options;
  const cached = readContentCacheEntry();

  if (!force && cached && Date.now() - cached.fetchedAt < appConfig.contentRevalidateMs) {
    return cached.data;
  }

  const controller = signal ? undefined : new AbortController();
  const abortSignal = signal ?? controller!.signal;
  const timeout = controller
    ? setTimeout(() => controller.abort(), appConfig.fetchTimeoutMs)
    : undefined;

  try {
    const res = await fetchContent(abortSignal, force ? undefined : cached ?? undefined);
    const validators = readResponseValidators(res);

    if (res.status === 304 && cached) {
      touchContentCache(validators);
      return cached.data;
    }

    if (!res.ok) {
      throw new Error(`Failed to load content (${res.status})`);
    }

    const json = (await res.json()) as WebsiteContent;
    validateContent(json);

    const signature = getContentSignature(json);
    if (cached && signature === cached.contentSignature) {
      touchContentCache(validators);
      return cached.data;
    }

    writeContentCache(json, validators);
    return json;
  } finally {
    if (timeout) clearTimeout(timeout);
  }
}
